import { Table, TableBody, TableCellProps, TableContainer, TableProps, TableRow } from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import { getComparator, SortOrder } from "../../utils/sort";
import EnhancedTableHead, { EnhancedTableHeadProps } from "./EnhancedTableHead";
import ExpandableTableRow, { ExpandableTableRowProps } from "./ExpandableTableRow";

export interface TableColumn extends TableCellProps {
	id: string;
	label: string;
	numeric?: boolean;
	sortable?: boolean;
	defaultSortOrder?: SortOrder;
}

type EnhancedTableProps<T> = {
	data: T[];
	getRowContent: (item: T) => ReactNode;
	getRowKey: (item: T) => string;
	getExpandedContent: (item: T) => ReactNode;
	expandColumnHeader?: string;
	showExpand?: boolean;
	defaultSortBy?: keyof T;
	defaultSortOrder?: SortOrder;
} & Pick<EnhancedTableHeadProps, "columns"> &
	Pick<ExpandableTableRowProps, "maxCollapseWidth"> &
	TableProps;

function EnhancedTable<T>({
	data,
	columns,
	expandColumnHeader,
	getRowContent,
	getRowKey,
	getExpandedContent,
	showExpand = true,
	maxCollapseWidth,
	defaultSortBy,
	defaultSortOrder,
	...tableProps
}: EnhancedTableProps<T>) {
	const columnsWithExpandColumn = useMemo(
		() => [...columns, { id: "expandColumn", label: expandColumnHeader ?? "" }],
		[columns, expandColumnHeader]
	);
	const [orderBy, setOrderBy] = useState<string>((defaultSortBy as string) ?? "");
	const [sortOrder, setSortOrder] = useState<SortOrder>(defaultSortOrder ?? "asc");

	const handleRequestSort = (event: React.MouseEvent<unknown>, property: TableColumn) => {
		const switchDirection = orderBy === property.id;
		const newSortOrder = switchDirection ? (sortOrder === "asc" ? "desc" : "asc") : property.defaultSortOrder;
		setSortOrder(newSortOrder ?? "asc");
		setOrderBy(property.id);
	};

	const sortedData = useMemo(
		() =>
			(data as any[])
				.slice()
				.sort(getComparator(sortOrder, orderBy))
				.map((item: T) =>
					showExpand ? (
						<ExpandableTableRow
							key={getRowKey(item).toString()}
							columnCount={columnsWithExpandColumn.length}
							expandedContent={getExpandedContent(item)}
							maxCollapseWidth={maxCollapseWidth}
						>
							{getRowContent(item)}
						</ExpandableTableRow>
					) : (
						<TableRow key={getRowKey(item).toString()}>{getRowContent(item)}</TableRow>
					)
				),
		[
			data,
			sortOrder,
			orderBy,
			columnsWithExpandColumn.length,
			getExpandedContent,
			getRowContent,
			getRowKey,
			maxCollapseWidth,
			showExpand,
		]
	);

	return (
		<TableContainer>
			<Table sx={tableProps.sx}>
				<EnhancedTableHead
					columns={showExpand ? columnsWithExpandColumn : columns}
					orderBy={orderBy}
					sortOrder={sortOrder}
					onRequestSort={handleRequestSort}
				/>
				<TableBody>{sortedData}</TableBody>
			</Table>
		</TableContainer>
	);
}

export default EnhancedTable;
